import { ErrorCodes, i18n, type NOSIDError, phoneService } from '@nosinovacao/nosid-mfe-common';
import { format } from 'date-fns';
import type z from 'zod';

export function getNameInitials(name: string): string {
  if (!name) return name;
  const nameParts = name.trim().split(' ');
  const firstName = nameParts[0].charAt(0).toUpperCase();
  if (nameParts.length === 1) {
    return firstName;
  }
  const lastName = nameParts.at(-1)?.charAt(0).toUpperCase();
  return firstName + lastName;
}

export function normalizeName(name: string): string {
  if (!name) return name;
  return name
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function normalizedFirstName(name: string): string {
  const normalizedName = normalizeName(name);
  if (!normalizedName) return name;
  return normalizedName.split(' ')[0] ?? normalizeName;
}

const dateFormats: Record<string, string> = {
  pt: 'dd/MM/yyyy',
  en: 'dd/MM/yyyy',
};

export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return '-';
  try {
    const d = new Date(date);
    return format(d, dateFormats[i18n.getSnapshot().language] ?? dateFormats.pt);
  } catch {
    return '-';
  }
}

export function parseDate(date: string | null | undefined): Date | null {
  if (!date) return null;
  try {
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) {
      return null;
    }
    return d;
  } catch {
    return null;
  }
}

export function formatPhone(phoneNumber: string) {
  const code = phoneService.parseCountryCode(phoneNumber);
  const digits = phoneService.parsePhoneNumber(phoneNumber, code);
  const parts = digits.match(/.{1,3}/g); // groups of 3 digits
  const formattedPhoneNumber = parts ? parts.join(' ') : digits;
  return [code.WithoutCode ? null : code.Code, formattedPhoneNumber]
    .filter(Boolean)
    .join(' ')
    .trim();
}

export type MobilePlatform = 'android' | 'ios';

export function getMobilePlatform(
  nav: Pick<Navigator, 'userAgent' | 'maxTouchPoints'> = globalThis.navigator,
): MobilePlatform | null {
  if (/android/i.test(nav.userAgent)) return 'android';
  if (/iphone|ipod|ipad/i.test(nav.userAgent)) return 'ios';
  // ? iPadOS 13+ reports a desktop Safari user agent, so touch support is the only reliable signal
  if (/macintosh/i.test(nav.userAgent) && nav.maxTouchPoints > 1) return 'ios';
  return null;
}

// ? on desktop the user agent describes the computer, so this only tells us which store to bet on
export function isAppleDevice(nav: Pick<Navigator, 'userAgent'> = globalThis.navigator): boolean {
  return /macintosh|mac os x|iphone|ipod|ipad/i.test(nav.userAgent);
}

export async function copyToClipboard(
  value: string,
  clipboard: Pick<Clipboard, 'writeText'> | undefined = globalThis.navigator?.clipboard,
): Promise<boolean> {
  // ? the Clipboard API is unavailable on insecure origins and in older browsers
  if (!clipboard) return false;
  try {
    await clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}

export function downloadTextFile(fileName: string, content: string): void {
  const url = URL.createObjectURL(new Blob([content], { type: 'text/plain;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

export function loadHistoryState<T, Throw extends boolean = true>(
  schema: z.ZodSchema<T>,
  options?: { throw?: Throw; errorCode?: ErrorCodes },
): (args: { location: { state: unknown } }) => Throw extends true ? T : T | null {
  return function ({ location: { state } }) {
    const parsingResult = schema.safeParse(state);
    if (parsingResult.success) {
      return parsingResult.data;
    }

    if (options?.throw) {
      const errorCode = options.errorCode ?? ErrorCodes.UnknownError;
      const err: NOSIDError = {
        ErrorCode: errorCode,
        Code: errorCode,
      };
      throw err;
    }

    return null as T;
  };
}
